import { coerceNumberProperty } from '@angular/cdk/coercion';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  InputSignal,
  linkedSignal,
  ResourceRef,
  Signal,
} from '@angular/core';
import { rxResource, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { AlertComponent, PageHeaderComponent, PaginatorComponent } from '@libs/common/components';
import { IconComponent } from '@libs/components/icons';
import {
  type OrderHistoryDeliveryType,
  type OrderHistoryFilter,
  type OrderHistoryFilterSortPageModel,
  type OrderHistoryModel,
  type OrderHistoryRecordModel,
  OrderHistorySortByOptions,
  type OrderHistorySortByValueType,
  ServicePaginationConfigDefaultLimit,
  ServicePaginationConfigLimits,
} from '@libs/services/models';
import { ViewportService } from '@libs/shared/services';
import { DeferredPageInfoTracking, OrderHistoryTrackingService } from '@libs/shared/tracking';
import { DateTime } from 'luxon';
import { Observable } from 'rxjs';
import { OrderHistoryDesktopComponent, OrderHistoryDesktopSkeletonComponent } from '../order-history-desktop';
import { OrderHistoryFilterSortComponent } from '../order-history-filter-sort';
import { OrderHistoryMobileComponent, OrderHistoryMobileSkeletonComponent } from '../order-history-mobile';
import { OrderHistoryService, OrderHistoryStateService } from '../services';
import { OrderHistoryOverviewRequestModel } from './order-history-overview-request.model';

@Component({
  selector: 'ui-order-history-overview',
  templateUrl: './order-history-overview.component.html',
  styleUrls: ['./order-history-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AlertComponent,
    IconComponent,
    OrderHistoryDesktopComponent,
    OrderHistoryDesktopSkeletonComponent,
    OrderHistoryFilterSortComponent,
    OrderHistoryMobileComponent,
    OrderHistoryMobileSkeletonComponent,
    PaginatorComponent,
    PageHeaderComponent,
  ],
  providers: [
    DeferredPageInfoTracking,
  ],
})
export class OrderHistoryOverviewComponent {
  public page: InputSignal<number | undefined> = input(undefined, { transform: this.convertNumberOrUndefined.bind(this) });
  public limit: InputSignal<number | undefined> = input(undefined, { transform: this.convertNumberOrUndefined.bind(this) });
  public sort: InputSignal<OrderHistorySortByValueType | undefined> = input(undefined, { transform: (x) => this.convertSortingOrUndefined(x) });
  public orderFrom: InputSignal<string | undefined> = input();
  public orderTo: InputSignal<string | undefined> = input();
  public shippingFrom: InputSignal<string | undefined> = input();
  public shippingTo: InputSignal<string | undefined> = input();
  public delivery: InputSignal<OrderHistoryDeliveryType | undefined> = input();

  public searchParams: Signal<OrderHistoryFilterSortPageModel> = computed(() => this.getNormalizedParams());
  public searchParams$: Observable<OrderHistoryFilterSortPageModel> = toObservable(this.searchParams);

  public orderHistoryResource: ResourceRef<OrderHistoryModel | undefined> = rxResource({
    params: () => this.searchParams(),
    stream: ({ params: searchParams }) => this.orderHistoryService.getOrderHistory(searchParams),
  });

  public readonly orderHistory: Signal<OrderHistoryModel | undefined> = linkedSignal(this.orderHistoryResource.value);
  public readonly orders: Signal<OrderHistoryRecordModel[]> = computed(() => this.orderHistory()?.orders ?? []);

  public isDesktop: Signal<boolean> = toSignal(this.viewportService.isDesktop(), { initialValue: true });

  private readonly orderHistoryTrackingService: OrderHistoryTrackingService = inject(OrderHistoryTrackingService);
  private readonly deferredPageInfoTracking: DeferredPageInfoTracking = inject(DeferredPageInfoTracking);

  public constructor(
    private readonly orderHistoryService: OrderHistoryService,
    private readonly orderHistoryStateService: OrderHistoryStateService,
    private readonly viewportService: ViewportService,
    private readonly router: Router,
    private readonly activatedRoute: ActivatedRoute,
  ) {
    effect(() => {
      this.checkForParams();
    });
    effect(() => {
      const params: OrderHistoryFilterSortPageModel = this.searchParams();
      const filterOrderFrom: string | undefined = this.orderFrom();
      const filterOrderTo: string | undefined = this.orderTo();
      const filterShippingFrom: string | undefined = this.shippingFrom();
      const filterShippingTo: string | undefined = this.shippingTo();

      params.page = this.page() ?? params.page;
      params.limit = this.limit() ?? params.limit;
      params.sort = this.sort() ?? params.sort;
      params.filter = {
        order: {
          from: filterOrderFrom ? DateTime.fromISO(filterOrderFrom) : params.filter?.order?.from,
          to: filterOrderTo ? DateTime.fromISO(filterOrderTo) : params.filter?.order?.to,
        },
        shipping: {
          from: filterShippingFrom ? DateTime.fromISO(filterShippingFrom) : params.filter?.shipping?.from,
          to: filterShippingTo ? DateTime.fromISO(filterShippingTo) : params.filter?.shipping?.to,
        },
        delivery: this.delivery() ?? params.filter?.delivery,
      };

      this.orderHistoryStateService.setParams(params);
    });
  }

  // -- Pagination

  public onPagination($event: PageEvent): void {
    const params: OrderHistoryFilterSortPageModel = this.searchParams();

    // The page index is 0-based, but the API is 1-based
    params.page = $event.pageIndex + 1;
    params.limit = $event.pageSize;
    this.orderHistoryStateService.setParams(params);
    this.navigateToParams(params);
  }

  // -- Sorting

  public onSortOrder(event?: string): void {
    const params: OrderHistoryFilterSortPageModel = this.searchParams();

    params.sort = event as OrderHistorySortByValueType;
    this.orderHistoryStateService.setParams(params);
    this.orderHistoryTrackingService.trackSort(params.sort);
    this.navigateToParams(params);
  }

  public onFilterChanged(event: OrderHistoryFilter): void {
    const params: OrderHistoryFilterSortPageModel = this.searchParams();

    params.filter = event;
    this.orderHistoryStateService.setParams(params);
    this.orderHistoryTrackingService.trackFilter(params.filter);
    this.navigateToParams(params);
  }

  private hasGetParams(): boolean {
    return Boolean(this.page() && this.limit() && this.sort());
  }

  private checkForParams(): void {
    if (!this.hasGetParams()) {
      void this.navigateToParams(this.searchParams());

      return;
    }

    this.deferredPageInfoTracking.complete();
  }

  private navigateToParams(params: OrderHistoryFilterSortPageModel): void {
    const queryParams: OrderHistoryOverviewRequestModel = {
      page: params.page,
      limit: params.limit,
      sort: params.sort,
      orderFrom: params.filter.order?.from?.toISODate() ?? undefined,
      orderTo: params.filter.order?.to?.toISODate() ?? undefined,
      shippingFrom: params.filter.shipping?.from?.toISODate() ?? undefined,
      shippingTo: params.filter.shipping?.to?.toISODate() ?? undefined,
      delivery: params.filter.delivery,
    };

    void this.router.navigate(
      [],
      {
        relativeTo: this.activatedRoute,
        queryParams,
        replaceUrl: true,
      },
    );
  }

  private getNormalizedParams(): OrderHistoryFilterSortPageModel {
    const params: OrderHistoryFilterSortPageModel = this.orderHistoryStateService.getParams();

    // dissolve the page param
    const page: number = this.page() ?? params.page;

    // dissolve the limit param
    let limit: number = this.limit() ?? params.limit;

    // validate the limit param
    if (!ServicePaginationConfigLimits.some(item => item === limit)) {
      limit = ServicePaginationConfigDefaultLimit;
    }

    // dissolve the sort order param
    const sort: OrderHistorySortByValueType = this.sort() ?? params.sort;

    // filter params
    const filterOrderFrom: string | undefined = this.orderFrom();
    const filterOrderTo: string | undefined = this.orderTo();
    const filterShippingFrom: string | undefined = this.shippingFrom();
    const filterShippingTo: string | undefined = this.shippingTo();
    const filter: OrderHistoryFilter = {
      order: {
        from: filterOrderFrom ? DateTime.fromISO(filterOrderFrom) : params.filter?.order?.from,
        to: filterOrderTo ? DateTime.fromISO(filterOrderTo) : params.filter?.order?.to,
      },
      shipping: {
        from: filterShippingFrom ? DateTime.fromISO(filterShippingFrom) : params.filter?.shipping?.from,
        to: filterShippingTo ? DateTime.fromISO(filterShippingTo) : params.filter?.shipping?.to,
      },
      delivery: this.delivery() ?? params.filter?.delivery,
    };

    return {
      ...params,
      page,
      limit,
      sort,
      filter,
    };
  }

  private convertNumberOrUndefined(value?: number): number | undefined {
    if (!value) {
      return undefined;
    }

    return coerceNumberProperty(value);
  }

  private convertSortingOrUndefined(value?: string): OrderHistorySortByValueType | undefined {
    if (!OrderHistorySortByOptions.some(item => item.value === value)) {
      return undefined;
    }

    return value as OrderHistorySortByValueType;
  }
}
